import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getVideoById, logEvent, updateReviewedStatus, updatePaidStatus, updateNeedsMore, getSettings, queryLogs } from '../services/api';
import GoogleDrivePlayer from '../components/GoogleDrivePlayer';
import HostedVideoPlayer from '../components/HostedVideoPlayer'; // Import the new player
import ReenactPanel from '../components/ReenactPanel';
import RecordingControls from '../components/RecordingControls';
import LoadingSpinner from '../components/LoadingSpinner';
import ManualUploadModal from '../components/ManualUploadModal';
import EditVideoModal from '../components/EditVideoModal';
import ReprocessModal from '../components/ReprocessModal';
import { formatDateTime, formatSeconds, formatCurrency } from '../utils/helpers';
import { videoCode } from '../utils/videoCode';
import { TimecodeText } from '../utils/timecode';
import { useAuth } from '../context/AuthContext';
import { useT } from '../utils/i18n';

const VideoDetailPage = () => {
  const { videoId } = useParams();
  const { isAdmin } = useAuth();
  // Only the narrator-visible strings are translated; everything inside an
  // `isAdmin &&` block is left as authored (see CONTEXT.md, "Display language").
  const t = useT();
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [isUpdatingReviewed, setIsUpdatingReviewed] = useState(false); // State for reviewed status update
  const [isUpdatingPaid, setIsUpdatingPaid] = useState(false); // State for paid status update

  // "Needs more" revert (admin sends a RECORDED take back to the NEW list to
  // collect extra detail, keeping the audio). Per-narrator draft/UI state.
  const [isUpdatingNeedsMore, setIsUpdatingNeedsMore] = useState(false);
  // Empty (not per-narrator '') so an untouched draft is `undefined` and falls
  // back to the stored note — seeding it with '' would render the editor blank
  // and save that emptiness over the note.
  const [needsMoreDraft, setNeedsMoreDraft] = useState({});
  const [revertBoxOpen, setRevertBoxOpen] = useState({ Won: false, Waan: false });
  const [noteCollapsed, setNoteCollapsed] = useState({ Won: false, Waan: false });
  // Past "อัดเพิ่ม" requests, newest first: the column keeps only the latest note,
  // so the history comes from the `needs_more` log events (admin-only listing).
  const [noteHistory, setNoteHistory] = useState({ Won: [], Waan: [] });
  const [historyOpen, setHistoryOpen] = useState({ Won: false, Waan: false });
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadNarrator, setUploadNarrator] = useState('Won');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);

  // Preload-the-whole-video setting and download progress. When preloading is on
  // for an S3-hosted video, recording is locked until the download finishes, so
  // buffering can never stall the video clock and drift the markers.
  const [preloadVideo, setPreloadVideo] = useState(false);
  const [videoDownloadDone, setVideoDownloadDone] = useState(false);
  const [videoDownloadFailed, setVideoDownloadFailed] = useState(false);
  const [videoDownloadPct, setVideoDownloadPct] = useState(0);

  // Reenact control handle on the player, plus the mutual-exclusion flags:
  // recording and reenacting can't run at once (both drive the same <video>).
  const playerHandleRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [reenactActive, setReenactActive] = useState(false);
  const [recordingNarrators, setRecordingNarrators] = useState({ Won: false, Waan: false });
  const recordingBusy = recordingNarrators.Won || recordingNarrators.Waan;

  const handleSeekToTimecode = useCallback((seconds) => {
    if (playerHandleRef.current && typeof playerHandleRef.current.seekTo === 'function') {
      playerHandleRef.current.seekTo(seconds);
    }
    if (playerContainerRef.current && typeof playerContainerRef.current.scrollIntoView === 'function') {
      playerContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const handleRecordingStateChange = useCallback((narrator, isRecording) => {
    setRecordingNarrators((prev) => (
      prev[narrator] === isRecording ? prev : { ...prev, [narrator]: isRecording }
    ));
  }, []);

  useEffect(() => {
    getSettings()
      .then((res) => setPreloadVideo(!!res.data.preload_video))
      .catch((err) => console.warn('Could not load settings; preload disabled:', err.message));
  }, []);

  // Reset download tracking when navigating to a different video.
  useEffect(() => {
    setVideoDownloadDone(false);
    setVideoDownloadFailed(false);
    setVideoDownloadPct(0);
  }, [videoId]);

  const handleDownloadProgress = useCallback((pct) => setVideoDownloadPct(pct), []);
  const handleDownloadComplete = useCallback(() => setVideoDownloadDone(true), []);
  const handleDownloadError = useCallback(() => setVideoDownloadFailed(true), []);

  const fetchVideoDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getVideoById(videoId);
      setVideo(response.data);
    } catch (err) {
      console.error("Failed to fetch video details:", err);
      setError(`${t('Failed to load video details')}: ${err.response?.data?.detail || err.message}. ` + (err.response?.status === 404 ? t('Video not found.') : t('Please try again later.')));
      setVideo(null); // Clear video on error
      if (err.response?.status === 404) {
        // Optional: Redirect if not found after a delay or show message permanently
        // setTimeout(() => navigate('/'), 3000);
      }
    } finally {
      setIsLoading(false);
    }
  }, [videoId, t]); // Depend on videoId

  const patchAudioDescription = useCallback((patch) => {
    setVideo((prev) => {
      if (!prev || !prev.audio_description) return prev;
      return { ...prev, audio_description: { ...prev.audio_description, ...patch } };
    });
  }, []);

  const refreshVideoSilently = useCallback(async () => {
    try {
      const response = await getVideoById(videoId);
      setVideo(response.data);
    } catch (err) {
      console.warn("Background refresh failed:", err.message);
      setActionError(`Could not refresh video details: ${err.response?.data?.detail || err.message}`);
    }
  }, [videoId]);

  useEffect(() => {
    fetchVideoDetails();
  }, [fetchVideoDetails]);

  // GET /logs/ is admin-only, and so is the history display — a failure here must
  // never break the page, so it degrades to "no history".
  const fetchNoteHistory = useCallback(async () => {
    if (!isAdmin) {
      setNoteHistory({ Won: [], Waan: [] });
      return;
    }
    const forNarrator = async (narrator) => {
      try {
        const res = await queryLogs({
          video_id: parseInt(videoId, 10), action: 'needs_more', narrator, limit: 50,
        });
        return res.data.items || [];
      } catch (err) {
        console.warn(`Could not load note history for ${narrator}:`, err.message);
        return [];
      }
    };
    const [won, waan] = await Promise.all([forNarrator('Won'), forNarrator('Waan')]);
    setNoteHistory({ Won: won, Waan: waan });
  }, [videoId, isAdmin]);

  useEffect(() => {
    fetchNoteHistory();
  }, [fetchNoteHistory]);

  const handlePlayerAction = useCallback(async (action, value = null) => {
    // This is called by child components (like RecordingControls when starting)
    if (!videoId) return;
    // The admin only reviews — their play/pause/seek must never become markers,
    // so an admin session logs no player events at all.
    if (isAdmin) return;
    try {
      await logEvent({ video_id: parseInt(videoId, 10), action, value });
      console.log(`Logged player action: ${action}`, value);
    } catch (err) {
      console.error("Failed to log player action:", action, err);
    }
  }, [videoId, isAdmin]); // Depend on videoId and admin state


  const [uploadNotification, setUploadNotification] = useState(null);
  
  const handleUploadSuccess = (updatedAudioDescription) => {
    console.log("Upload success, updating video state:", updatedAudioDescription);
    setActionError(null);
    // Show success notification
    setUploadNotification({
      type: 'success',
      message: t('Recording uploaded successfully!')
    });

    // Clear notification after 5 seconds
    setTimeout(() => setUploadNotification(null), 5000);

    refreshVideoSilently();
  };

  const handleToggleReviewedStatus = async (narrator) => {
    if (!video || !video.audio_description) return;

    setIsUpdatingReviewed(true);
    setActionError(null);
    let currentStatus = false;
    if (narrator === "Waan") {
      currentStatus = video.audio_description.is_reviewed_waan;
    } else {
      currentStatus = video.audio_description.is_reviewed;
    }
    const nextStatus = !currentStatus;
    const recordingId = video.audio_description.id;

    try {
      const res = await updateReviewedStatus(recordingId, narrator, nextStatus);
      const updated = res?.data || {};
      const patch = {};
      if (updated.is_reviewed !== undefined) patch.is_reviewed = updated.is_reviewed;
      if (updated.is_reviewed_waan !== undefined) patch.is_reviewed_waan = updated.is_reviewed_waan;
      if (Object.keys(patch).length === 0) {
        if (narrator === "Waan") patch.is_reviewed_waan = nextStatus;
        else patch.is_reviewed = nextStatus;
      }
      patchAudioDescription(patch);
      refreshVideoSilently();
    } catch (err) {
      console.error("Failed to update reviewed status:", err);
      setActionError(`Failed to update reviewed status: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsUpdatingReviewed(false);
    }
  };

  const handleTogglePaidStatus = async (narrator) => {
    if (!video || !video.audio_description) return;

    setIsUpdatingPaid(true);
    setActionError(null);
    let currentStatus = false;
    if (narrator === "Waan") {
      currentStatus = video.audio_description.is_paid_waan;
    } else {
      currentStatus = video.audio_description.is_paid;
    }
    const nextStatus = !currentStatus;
    const recordingId = video.audio_description.id;

    try {
      const res = await updatePaidStatus(recordingId, narrator, nextStatus);
      const updated = res?.data || {};
      const patch = {};
      if (updated.is_paid !== undefined) patch.is_paid = updated.is_paid;
      if (updated.is_paid_waan !== undefined) patch.is_paid_waan = updated.is_paid_waan;
      if (Object.keys(patch).length === 0) {
        if (narrator === "Waan") patch.is_paid_waan = nextStatus;
        else patch.is_paid = nextStatus;
      }
      patchAudioDescription(patch);
      refreshVideoSilently();
    } catch (err) {
      console.error("Failed to update paid status:", err);
      setActionError(`Failed to update paid status: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsUpdatingPaid(false);
    }
  };

  // Set/clear the per-narrator "needs more" revert flag. `note` is written when a
  // string is passed, left unchanged when null.
  const handleSetNeedsMore = async (narrator, needsMore, note) => {
    if (!video || !video.audio_description) return;
    setIsUpdatingNeedsMore(true);
    setActionError(null);
    const recordingId = video.audio_description.id;
    try {
      const res = await updateNeedsMore(recordingId, narrator, needsMore, note);
      const updated = res?.data || {};
      const patch = {};
      if (updated.needs_more !== undefined) patch.needs_more = updated.needs_more;
      if (updated.needs_more_waan !== undefined) patch.needs_more_waan = updated.needs_more_waan;
      if (updated.needs_more_note !== undefined) patch.needs_more_note = updated.needs_more_note;
      if (updated.needs_more_note_waan !== undefined) patch.needs_more_note_waan = updated.needs_more_note_waan;
      if (Object.keys(patch).length === 0) {
        if (narrator === "Waan") {
          patch.needs_more_waan = needsMore;
          if (typeof note === "string") patch.needs_more_note_waan = note;
        } else {
          patch.needs_more = needsMore;
          if (typeof note === "string") patch.needs_more_note = note;
        }
      }
      patchAudioDescription(patch);
      setRevertBoxOpen((prev) => ({ ...prev, [narrator]: false }));
      // Drop the draft so the editor re-reads the note that was just saved.
      setNeedsMoreDraft((prev) => {
        const next = { ...prev };
        delete next[narrator];
        return next;
      });
      refreshVideoSilently();
      fetchNoteHistory();
    } catch (err) {
      console.error("Failed to update needs_more status:", err);
      setActionError(`Failed to update "needs more" status: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsUpdatingNeedsMore(false);
    }
  };

  const handleOpenUploadModal = (narrator) => {
    setUploadNarrator(narrator);
    setShowUploadModal(true);
  };

  const handleUploadModalSuccess = () => {
    setActionError(null);
    setUploadNotification({
      type: 'success',
      message: `Manual upload for ${uploadNarrator} completed successfully!`
    });
    setTimeout(() => setUploadNotification(null), 5000);
    refreshVideoSilently();
  };

  const handleReprocessSuccess = (updatedAudioDescription) => {
    setActionError(null);
    if (updatedAudioDescription) patchAudioDescription(updatedAudioDescription);
    setUploadNotification({
      type: 'success',
      message: 'Audio reprocessed successfully!'
    });
    setTimeout(() => setUploadNotification(null), 5000);
    refreshVideoSilently();
  };

  const handleEditModalSuccess = () => {
    setActionError(null);
    setUploadNotification({
      type: 'success',
      message: 'Video metadata updated successfully!'
    });
    setTimeout(() => setUploadNotification(null), 5000);
    refreshVideoSilently();
  };


  if (isLoading) {
    return (
      <div className="text-center">
        <LoadingSpinner />
        <p>{t('Loading video details...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error} <Link to="/" className="alert-link">{t('Go back to list')}</Link>
      </div>
    );
  }

  if (!video) {
    // Should be covered by error state, but as a fallback
    return <div className="alert alert-warning">{t('Video data not available.')} <Link to="/">{t('Go back to list')}</Link></div>;
  }

  const hasExistingAD = !!video.audio_description;
  const hasExistingADWaan = hasExistingAD && !!video.audio_description.s3_presigned_url_waan;
  const hasExistingADWon = hasExistingAD && !!video.audio_description.s3_presigned_url;
  const isPaid = hasExistingAD && video.audio_description.is_paid;
  const isPaidWaan = hasExistingAD && video.audio_description.is_paid_waan;
  const isReviewed = hasExistingAD && video.audio_description.is_reviewed;
  const isReviewedWaan = hasExistingAD && video.audio_description.is_reviewed_waan;

  const isGoogleDriveVideo = video.google_drive_link.startsWith('https://drive.google.com');

  // Preload only applies to the HTML5 player (an S3-hosted file we can fetch),
  // not the Google Drive iframe. Recording stays locked until the download ends
  // (or fails, in which case we fall back to streaming and unlock anyway). The
  // admin only reviews — never records — so they always stream and never write
  // to the video cache.
  const preloadActive = preloadVideo && !isGoogleDriveVideo && !isAdmin;
  const videoReady = !preloadActive || videoDownloadDone || videoDownloadFailed;
  // Recording is locked while the video is still preloading OR a reenact is
  // running (they'd fight over the same <video> element).
  const recordLocked = !videoReady || reenactActive;
  const recordLockReason = !videoReady
    ? `กำลังโหลดวิดีโอ... ${videoDownloadPct}%`
    : (reenactActive ? t('Reenact is running — stop it to record.') : null);

  const narratorLabel = (narrator) => (narrator === 'Waan' ? 'หวาน' : 'ว่าน');

  // Every past "อัดเพิ่ม" request for a narrator, newest first. Renders under the
  // control in *all* states — the note outlives the flag, so after the narrator
  // records the follow-up this is the only place it is still visible.
  const renderNoteHistory = (narrator, fallbackNote) => {
    const rows = noteHistory[narrator] || [];
    // Notes written before the history log existed have no rows: show the stored
    // column value so they are not lost from the UI.
    const items = rows.length > 0
      ? rows.map((row) => ({ key: row.id, note: row.value, at: row.timestamp }))
      : (fallbackNote ? [{ key: 'stored', note: fallbackNote, at: null }] : []);
    if (items.length === 0) return null;

    const label = narratorLabel(narrator);
    const open = historyOpen[narrator];
    return (
      <div className="mb-2 small">
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-decoration-none"
          onClick={() => setHistoryOpen((prev) => ({ ...prev, [narrator]: !open }))}
        >
          📝 เคยขอให้อัดเพิ่ม ({label}) × {items.length} {open ? '▲' : '▼'}
        </button>
        {open && (
          <ul className="list-unstyled mb-0 mt-1 ps-2 border-start">
            {items.map((item) => (
              <li key={item.key} className="mb-1">
                {item.at && <span className="text-muted me-2">{formatDateTime(item.at)}</span>}
                <span style={{ whiteSpace: 'pre-wrap' }}>
                  <TimecodeText text={item.note} onSeek={handleSeekToTimecode} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // Admin control: revert a RECORDED take to NEW (keeping audio), edit the note
  // while reverted, or flip it back to RECORDED. Shows past notes as history.
  const renderNeedsMoreControl = (narrator) => {
    if (!hasExistingAD) return null;
    const ad = video.audio_description;
    const label = narratorLabel(narrator);
    const reviewed = narrator === 'Waan' ? ad.is_reviewed_waan : ad.is_reviewed;
    const paid = narrator === 'Waan' ? ad.is_paid_waan : ad.is_paid;
    const hasAudio = narrator === 'Waan' ? hasExistingADWaan : hasExistingADWon;
    const needsMore = narrator === 'Waan' ? ad.needs_more_waan : ad.needs_more;
    const note = (narrator === 'Waan' ? ad.needs_more_note_waan : ad.needs_more_note) || '';
    const canRevert = hasAudio && !reviewed && !paid && !needsMore; // i.e. currently RECORDED
    const draft = needsMoreDraft[narrator] !== undefined ? needsMoreDraft[narrator] : note;

    // The state-specific control; the history is appended to whichever one wins,
    // so it can never be shadowed by an earlier branch returning first.
    const control = (() => {
      if (needsMore) {
        return (
          <div className="p-2 mb-2 border rounded bg-white">
            <div className="fw-bold mb-1">↩️ อยู่ในสถานะ “อัดเพิ่ม” ({label})</div>
            <textarea
              className="form-control form-control-sm mb-2"
              rows={2}
              placeholder="สิ่งที่ต้องอัดเพิ่ม..."
              value={draft}
              onChange={(e) => setNeedsMoreDraft((prev) => ({ ...prev, [narrator]: e.target.value }))}
            />
            <button
              className="btn btn-sm btn-primary me-2"
              disabled={isUpdatingNeedsMore}
              onClick={() => handleSetNeedsMore(narrator, true, draft)}
            >
              💾 บันทึกโน้ต
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={isUpdatingNeedsMore}
              onClick={() => handleSetNeedsMore(narrator, false, null)}
            >
              กลับเป็น Recorded
            </button>
          </div>
        );
      }

      if (canRevert) {
        const open = revertBoxOpen[narrator];
        if (!open) {
          return (
            <button
              className="btn btn-sm btn-outline-warning me-2"
              disabled={isUpdatingNeedsMore}
              onClick={() => {
                setNeedsMoreDraft((prev) => ({ ...prev, [narrator]: note }));
                setRevertBoxOpen((prev) => ({ ...prev, [narrator]: true }));
              }}
            >
              ↩️ ส่งกลับไปหน้า New เพื่ออัดเพิ่ม ({label})
            </button>
          );
        }
        return (
          <div className="p-2 mb-2 border rounded bg-white">
            <div className="fw-bold mb-1">สิ่งที่ต้องอัดเพิ่ม ({label})</div>
            <textarea
              className="form-control form-control-sm mb-2"
              rows={2}
              placeholder="ระบุสิ่งที่ต้องให้อัดเพิ่ม (จะแสดงให้ผู้อัดเห็น)..."
              value={draft}
              onChange={(e) => setNeedsMoreDraft((prev) => ({ ...prev, [narrator]: e.target.value }))}
            />
            <button
              className="btn btn-sm btn-warning me-2"
              disabled={isUpdatingNeedsMore}
              onClick={() => handleSetNeedsMore(narrator, true, draft)}
            >
              ↩️ ยืนยันส่งกลับ
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={isUpdatingNeedsMore}
              onClick={() => setRevertBoxOpen((prev) => ({ ...prev, [narrator]: false }))}
            >
              ยกเลิก
            </button>
          </div>
        );
      }

      // Reviewed or paid, or no audio for this narrator: no control, history only.
      return null;
    })();

    const history = renderNoteHistory(narrator, note);
    if (!control && !history) return null;
    return <>{control}{history}</>;
  };

  // Narrator-facing banner: prominent but non-obstructive (normal flow above the
  // player, collapsible). Shows only while reverted with a non-empty note.
  const renderNarratorNote = (narrator) => {
    if (!hasExistingAD) return null;
    const ad = video.audio_description;
    const needsMore = narrator === 'Waan' ? ad.needs_more_waan : ad.needs_more;
    const note = (narrator === 'Waan' ? ad.needs_more_note_waan : ad.needs_more_note) || '';
    if (!needsMore || !note.trim()) return null;
    const label = narratorLabel(narrator);
    const collapsed = noteCollapsed[narrator];
    return (
      <div key={narrator} className="alert alert-warning mb-2">
        <div className="d-flex justify-content-between align-items-center">
          <strong>🎙️ สิ่งที่ต้องอัดเพิ่ม ({label})</strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-dark ms-2"
            onClick={() => setNoteCollapsed((prev) => ({ ...prev, [narrator]: !collapsed }))}
          >
            {collapsed ? 'ดู' : 'ซ่อน'}
          </button>
        </div>
        {!collapsed && (
          <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
            <TimecodeText text={note} onSeek={handleSeekToTimecode} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="video-detail-page">
      <Link to="/">{t('« Back to Video List')}</Link>
      {actionError && (
        <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert" aria-live="assertive">
          {actionError}
          <button type="button" className="btn-close" aria-label="Dismiss error" onClick={() => setActionError(null)}></button>
        </div>
      )}
      <h2 className="mt-3">
        {video.name}
        {isAdmin && (
          <button
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={() => setShowEditModal(true)}
            title="Edit video"
          >
            ✏️ Edit
          </button>
        )}
      </h2>
      <div className="row mb-3">
        <div className="col-md-6">
          <p><strong>{t('ID:')}</strong> {video.id}</p>
          <p><strong>{t('Code:')}</strong> {videoCode(video)}</p>
          <p><strong>{t('Length:')}</strong> {formatSeconds(video.length_seconds)}</p>
          <p><strong>{t('Cost (Won):')}</strong> {formatCurrency(video.cost_thb)}</p>
          <p><strong>{t('Cost (Waan):')}</strong> {formatCurrency(video.cost_thb_waan)}</p>
        </div>
        <div className="col-md-6">
          <p><strong>{t('Videoe Link:')}</strong> <a href={video.google_drive_link} target="_blank" rel="noopener noreferrer">{t('Open Link')}</a></p>
          <p><strong>{t('Created:')}</strong> {formatDateTime(video.created_at)}</p>
          <p><strong>{t('Last Updated:')}</strong> {formatDateTime(video.updated_at)}</p>
        </div>
      </div>

      <div className="card bg-light p-3 mb-3">
        <p><strong>{t('Status:')}</strong> <span className={`badge ${video.status === 'new' ? 'bg-warning text-dark' : video.status === 'recorded' ? 'bg-primary' : 'bg-success'}`}>{t(video.status.toUpperCase())}</span></p>

        <>
          {hasExistingADWon && (
            <>
            <p><strong>ไฟล์ว่านอัด:</strong> <a href={video.audio_description.s3_presigned_url} target="_blank" rel="noopener noreferrer">{t('download')}</a></p>
            <p><strong>ส่งครั้งสุดท้าย:</strong> {formatDateTime(video.audio_description.last_updated_at)}</p>
            </>
          )}


          {hasExistingADWaan && (
            <>
            <p><strong>ไฟล์หวานอัด:</strong> <a href={video.audio_description.s3_presigned_url_waan} target="_blank" rel="noopener noreferrer">{t('download')}</a></p>
            <p><strong>ส่งครั้งสุดท้าย:</strong> {formatDateTime(video.audio_description.last_updated_at_waan)}</p>
            </>
          )}

          <p><strong>ตรวจของว่าน:</strong> {isReviewed ? t('Yes') : t('No')}</p>
          <p><strong>ตรวจของหวาน:</strong> {isReviewedWaan ? t('Yes') : t('No')}</p>
          <p><strong>จ่ายของว่าน:</strong> {isPaid ? t('Yes') : t('No')}</p>
          <p><strong>จ่ายของหวาน:</strong> {isPaidWaan ? t('Yes') : t('No')}</p>
          
          {/* Review / pay / manual-upload / reprocess — admin only. */}
          {isAdmin && (
          <>
          {/* Reviewed buttons */}
          <div className="mb-2">
            <button
              className={`btn btn-sm me-2 ${isReviewed ? 'btn-warning' : 'btn-success'}`}
              onClick={() => handleToggleReviewedStatus('Won')}
              disabled={isUpdatingReviewed || !hasExistingAD}
            >
              {isUpdatingReviewed ? <LoadingSpinner small /> : (isReviewed ? 'Mark as not reviewed (Won)' : 'Mark as reviewed (Won)')}
            </button>
            <button
              className={`btn btn-sm ${isReviewedWaan ? 'btn-warning' : 'btn-success'}`}
              onClick={() => handleToggleReviewedStatus('Waan')}
              disabled={isUpdatingReviewed || !hasExistingAD}
            >
              {isUpdatingReviewed ? <LoadingSpinner small /> : (isReviewedWaan ? 'Mark as not reviewed (Waan)' : 'Mark as reviewed (Waan)')}
            </button>
          </div>
          
          {/* Paid buttons */}
          <div className="mb-2">
            <button
              className={`btn btn-sm me-2 ${isPaid ? 'btn-danger' : 'btn-info'}`}
              onClick={() => handleTogglePaidStatus('Won')}
              disabled={isUpdatingPaid || !hasExistingAD}
            >
              {isUpdatingPaid ? <LoadingSpinner small /> : (isPaid ? 'Mark as not paid (Won)' : 'Mark as paid (Won)')}
            </button>
            <button
              className={`btn btn-sm ${isPaidWaan ? 'btn-danger' : 'btn-info'}`}
              onClick={() => handleTogglePaidStatus('Waan')}
              disabled={isUpdatingPaid || !hasExistingAD}
            >
              {isUpdatingPaid ? <LoadingSpinner small /> : (isPaidWaan ? 'Mark as not paid (Waan)' : 'Mark as paid (Waan)')}
            </button>
          </div>
          
          {/* Manual upload buttons */}
          <div className="mb-2">
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => handleOpenUploadModal('Won')}
            >
              📁 Manual Upload (Won)
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleOpenUploadModal('Waan')}
            >
              📁 Manual Upload (Waan)
            </button>
          </div>

          {/* Reprocess button */}
          {hasExistingAD && (
            <div className="mb-2">
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => setShowReprocessModal(true)}
              >
                🔄 Reprocess Audio
              </button>
            </div>
          )}

          {/* Revert to New ("needs more detail") — keeps the audio for appending */}
          {hasExistingAD && (
            <div className="mb-2">
              {renderNeedsMoreControl('Won')}
              {renderNeedsMoreControl('Waan')}
            </div>
          )}

          {(isUpdatingReviewed || isUpdatingPaid || isUpdatingNeedsMore) && <span className="ms-2 text-muted">Updating...</span>}
          </>
          )}
        </>
        {!hasExistingAD && <p>{t('No audio description recorded yet.')}</p>}
      </div>


      <div className="row">
        <div className="col-lg-8" ref={playerContainerRef}>
        {/* Narrator-facing "record more" notes — above the player, non-obstructive. */}
        {renderNarratorNote('Won')}
        {renderNarratorNote('Waan')}
        {isGoogleDriveVideo ? (
          <GoogleDrivePlayer
            videoUrl={video.google_drive_link}
            videoId={video.id}
            onPlayerAction={handlePlayerAction}
          />
        ) : (
          <HostedVideoPlayer
          ref={playerHandleRef}
          videoUrl={video.google_drive_link}
          videoId={video.id}
          onPlayerAction={handlePlayerAction}
          preloadEnabled={preloadActive}
          onDownloadProgress={handleDownloadProgress}
          onDownloadComplete={handleDownloadComplete}
          onDownloadError={handleDownloadError}
        />
      )}
        {!isGoogleDriveVideo && (
          <ReenactPanel
            videoId={video.id}
            playerRef={playerHandleRef}
            narrators={{ Won: hasExistingADWon, Waan: hasExistingADWaan }}
            disabled={recordingBusy}
            disabledReason={recordingBusy ? t('Recording in progress — stop it to reenact.') : null}
            onActiveChange={setReenactActive}
          />
        )}
        </div>
        <div className="col-lg-4">
          <h4>{t('Audio Description Recording')}</h4>
          
          {uploadNotification && (
            <div className={`alert alert-${uploadNotification.type} alert-dismissible fade show`}>
              {uploadNotification.message}
              <button type="button" className="btn-close" onClick={() => setUploadNotification(null)}></button>
            </div>
          )}
          
          <RecordingControls
            videoId={video.id}
            hasExistingAD={hasExistingADWaan}
            narrator={"Waan"}
            onUploadSuccess={handleUploadSuccess}
            recordLocked={recordLocked}
            recordLockReason={recordLockReason}
            onRecordingStateChange={handleRecordingStateChange}
          />
          <RecordingControls
            videoId={video.id}
            hasExistingAD={hasExistingADWon}
            narrator={"Won"}
            onUploadSuccess={handleUploadSuccess}
            recordLocked={recordLocked}
            recordLockReason={recordLockReason}
            onRecordingStateChange={handleRecordingStateChange}
          />

          <h3>คำถาม</h3>
          <ul>
            <li>ถ้าน้องเป็นตุ๊กตา น้องเป็นตุ๊กตาอะไร สีอะไร ท่าอะไร ขนเป็นแบบไหน สภาพน้องตอนแรกเป็นยังไง ตอนจบเป็นยังไง ตัวใหญ่แค่ไหน (ลองประมาณ) น้องมีรูมั้ย ถ้ามีเป็นรูแบบไหน รูอยู่ตรงไหน น้องตุยมั้ย น่ารักมั้ย</li>
            <li>ถ้าน้องเป็นเป่าลม น้องเป็นตัวอะไร สีอะไร ท่าอะไร ตัวใหญ่แค่ไหน (ลองประมาณ) น้องตุยมั้ย เค้าเป่าน้องเต่งมั้ย สภาพน้องเป็นไง ผ่านการใช้มาบ้างไหม มีรูไหม ถ้ามีเป็นยังไง อยู่ตรงไหน</li>
            <li>บรรยายทั่วไป เค้าทำอะไรกับน้องบ้าง (พยายามอธิบายท่า เช่นเค้านั่งคร่อมน้อง) ถ้าเค้าขย่มน้องเค้าขย่มตรงไหนของน้อง ถ้าเค้าฉีกน้องเค้าฉีกตรงไหน (ถ้าเค้าฉีก / เจาะน้อง ถ้าตอนที่น้องตุยแล้วฝากบอกด้วย)</li>
            <li>พยายามไม่ใช้คำบรรยายกว้างๆ เช่น เค้ากระแทกๆ น้อง เค้าเอาน้องมาถูๆ อะไรแบบนี้ ให้พยายามเจอะจง เช่น เค้าเอาจู๋กระแทกตรงหน้าของน้อง หรือเค้าเอาหัวน้องมาถูๆ กับจู๋ พยายามโฟกัสว่าจู๋เค้าโดนตรงส่วนไหนของน้อง อะไรแบบนี้</li>
            <li>พยายามอย่าปล่อยเงียบยาวๆ ลองหาอย่างอื่นอธิบายเสิรมเช่น ถ้าเค้ากระแทกน้องอยู่ หลังบรรยายตอนกระแทกไปแล้ว อาจจะบรรยายเพิ่มว่าเค้าทำเร็วทำช้า เค้าทำจนน้องยุบเลย อะไรแบบนี้</li>
            <li>เซ็ค เค้าแตกมั้ย แตกใส่ตรงไหนของน้อง เค้าเอาจูออกมาหรือปล่าว ถ้าเค้าฉี่ให้บอกว่าฉี่ใส่ตรงไหน น้องเลอะเยอะมั้ย สงสารน้องมั้ย</li>
          </ul>
        </div>
      </div>

      {/* Modals */}
      <ManualUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        videoId={video.id}
        narrator={uploadNarrator}
        onUploadSuccess={handleUploadModalSuccess}
      />
      
      <EditVideoModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        video={video}
        onSaveSuccess={handleEditModalSuccess}
      />

      <ReprocessModal
        show={showReprocessModal}
        onHide={() => setShowReprocessModal(false)}
        video={video}
        onReprocessSuccess={handleReprocessSuccess}
      />
    </div>
  );
};

export default VideoDetailPage;